import { isOpenStatus, OUTCOME_REMARKS } from "./constants";
import { toMins } from "./timeUtils";

export const REMARK_PATTERN = /^\[([A-Z0-9/\w\s.]+) (\d+:\d+)\] (.+)$/i;

export function terminalFallback(airline) {
  if (!airline) return "TBIT";
  return airline.toUpperCase() === "AC" ? "T6" : "TBIT";
}

/**
 * Appends a timestamped, attributed remark to an existing remark string.
 * Format: "[AIRLINE/Name HH:MM] text"
 */
export function appendRemark(existingRemark, text, airline, sccoName) {
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const who = sccoName ? airline + "/" + sccoName : airline;
  const tag = "[" + who + " " + time + "] " + text.trim();
  const sep = String.fromCharCode(10);
  return existingRemark ? existingRemark + sep + tag : tag;
}

// Fields synced to all entries sharing the same outbound or inbound flight number.
const SHARED_OUT_FIELDS = ["outFlt", "outAirline", "dest", "outGate", "std", "etd", "atd"];
const SHARED_IN_FIELDS  = ["inFlt",  "inAirline",  "origin", "inGate", "sta", "eta", "ata"];

/**
 * Finds all entries that share the same outbound or inbound flight as `updated`,
 * and returns a merged list of sibling entries with the shared fields patched in.
 * Entries that share both flights get both patches applied.
 */
export function findSiblingUpdates(updated, entries) {
  const hasSameOutFlt = (e) =>
    e.id !== updated.id &&
    e.outFlt && updated.outFlt &&
    e.outFlt.trim().toUpperCase() === updated.outFlt.trim().toUpperCase() &&
    (e.outAirline || "").toUpperCase() === (updated.outAirline || "").toUpperCase();

  const hasSameInFlt = (e) =>
    e.id !== updated.id &&
    e.inFlt && updated.inFlt &&
    e.inFlt.trim().toUpperCase() === updated.inFlt.trim().toUpperCase() &&
    (e.inAirline || "").toUpperCase() === (updated.inAirline || "").toUpperCase();

  const outPatch = Object.fromEntries(SHARED_OUT_FIELDS.map(k => [k, updated[k] ?? ""]));
  const inPatch  = Object.fromEntries(SHARED_IN_FIELDS.map(k =>  [k, updated[k] ?? ""]));

  const outSiblings = entries.filter(hasSameOutFlt);
  const inSiblings  = entries.filter(hasSameInFlt)
    .filter(sib => !outSiblings.some(s => s.id === sib.id));

  const mergedOut = outSiblings.map(sib =>
    hasSameInFlt(sib) ? { ...sib, ...outPatch, ...inPatch } : { ...sib, ...outPatch }
  );

  return [...mergedOut, ...inSiblings.map(sib => ({ ...sib, ...inPatch }))];
}

/**
 * Compares an imported entry against its existing counterpart and returns only
 * the DB-column fields that have actually changed. Returns null if nothing changed.
 */
export function buildImportPatch(imported, existing) {
  const patch = {};
  if (imported.eta && imported.eta !== existing.eta) patch.eta         = imported.eta;
  if (imported.sta        !== existing.sta)          patch.sta         = imported.sta        || "";
  if (imported.std        !== existing.std)          patch.std         = imported.std        || "";
  if (imported.etd        !== existing.etd)          patch.etd         = imported.etd        || "";
  if (imported.ttlPax     !== existing.ttlPax)       patch.ttl_pax     = imported.ttlPax     || "";
  if (imported.ttlBags    !== existing.ttlBags)      patch.ttl_bags    = imported.ttlBags    || "";
  if (imported.origin     !== existing.origin)       patch.origin      = imported.origin     || "";
  if (imported.dest       !== existing.dest)         patch.dest        = imported.dest       || "";
  if (imported.inAirline  !== existing.inAirline)    patch.in_airline  = imported.inAirline  || "";
  if (imported.outAirline !== existing.outAirline)   patch.out_airline = imported.outAirline || "";
  if (imported.airline    !== existing.airline)      patch.airline     = imported.airline    || "";
  if (imported.section    !== existing.section)      patch.section     = imported.section    || "";
  if (imported.cancelled  !== existing.cancelled)    patch.cancelled   = imported.cancelled  || false;
  if (imported.inGate  && !existing.inGate)          patch.in_gate     = imported.inGate;
  if (imported.outGate && !existing.outGate)         patch.out_gate    = imported.outGate;
  if (imported.ata     && !existing.ata)             patch.ata         = imported.ata;
  if (imported.atd     && !existing.atd)             patch.atd         = imported.atd;
  // Same TDT expedite rules as the sync (_build_import_patch in tdt-sync/ha-addon/tdt_sync.py).
  // Monitoring is set-only: TDT's in-progress count drops to 0 once the expedite resolves,
  // and the card should keep its flag.
  if (imported.monitoring && !existing.monitoring)   patch.monitoring  = true;
  // Expedite outcome → Pending review, never straight into status. Skip if the card is
  // already complete, already shows this suggestion, or an SCCO rejected this exact outcome.
  const outcome = imported.pendingStatus;
  if (outcome && isOpenStatus(existing.status)
      && outcome !== existing.pendingStatus && outcome !== existing.pendingDismissed) {
    patch.pending_status = outcome;
    patch.remark = appendRemark(existing.remark, OUTCOME_REMARKS[outcome], "TDT");
  }
  // Pax expedited from TDT's success count: fill only, never overwrite an SCCO's count
  if (outcome && !existing.paxExp && outcome !== existing.pendingDismissed)
    patch.pax_exp = imported.paxExp;
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * Gate / time changes between two polls of the same cards, for open, non-cancelled cards only.
 * Returns Map<id, [{ kind, label }]>, e.g. { kind: "outGate", label: "GATE 74→72" }, { kind: "eta", label: "ETA +25" }.
 * Times count only when they move by 15+ minutes; gates only when both old and new are known.
 */
export function detectChanges(prevEntries, nextEntries) {
  const prevById = new Map(prevEntries.map(e => [e.id, e]));
  const moved = (a, b) => {
    const am = toMins(a), bm = toMins(b);
    if (am === null || bm === null) return null;
    let d = bm - am;
    if (d > 720) d -= 1440;   // crossed midnight backwards
    if (d < -720) d += 1440;  // crossed midnight forwards
    return Math.abs(d) >= 15 ? d : null;
  };
  const changes = new Map();
  for (const next of nextEntries) {
    const prev = prevById.get(next.id);
    if (!prev || !isOpenStatus(next.status) || next.cancelled || next.atd === "XCL") continue;
    const found = [];
    if (prev.inGate && next.inGate && prev.inGate !== next.inGate)
      found.push({ kind: "inGate", label: `ARR GATE ${prev.inGate}→${next.inGate}` });
    if (prev.outGate && next.outGate && prev.outGate !== next.outGate)
      found.push({ kind: "outGate", label: `GATE ${prev.outGate}→${next.outGate}` });
    for (const [kind, name] of [["eta", "ETA"], ["etd", "ETD"], ["std", "STD"]]) {
      const d = moved(prev[kind], next[kind]);
      if (d !== null) found.push({ kind, label: `${name} ${d > 0 ? "+" : "−"}${Math.abs(d)}` });
    }
    if (found.length) changes.set(next.id, found);
  }
  return changes;
}

// Named passengers grouped by booked cabin (First, Business, Economy, then any without
// one, e.g. added by hand), A–Z within each group. Each item keeps its index in the
// original list so editors can still address it. `showHeading` is false only for a
// lone cabin-less group (old cards), where a heading would add nothing.
const CABIN_LABELS = { F: "First", J: "Business", Y: "Economy" };
export function paxByCabin(passengers) {
  const groups = ["F", "J", "Y", ""].map(cabin => ({ cabin, label: CABIN_LABELS[cabin] || "Other", items: [] }));
  (passengers || []).forEach((p, i) => {
    groups.find(g => g.cabin === (CABIN_LABELS[p.cabin] ? p.cabin : "")).items.push({ p, i });
  });
  const byName = (a, b) => (!a.p.name) - (!b.p.name) || (a.p.name || "").localeCompare(b.p.name || "");
  const used = groups.filter(g => g.items.length);
  return used.map(g => ({ ...g, items: g.items.sort(byName), showHeading: used.length > 1 || g.cabin !== "" }));
}

// Gate-sign names for Expeditor Mode, from TDT passenger names ("LAST/FIRST [TITLE]").
// One passenger per surname → "J. Monas" (first initial + surname, so the full name isn't on
// public display). Several with the same surname → "Johnson Party of 4". Sorted A–Z by surname.
const NAME_TITLES = new Set(["MR", "MRS", "MS", "MISS", "MSTR", "MSTER", "MASTER", "DR", "PROF", "REV", "SIR", "CHD", "INF"]);

function titleCaseName(s) {
  return s.toLowerCase().replace(/(^|[\s\-'’])([a-z])/g, (m, sep, c) => sep + c.toUpperCase());
}

function splitPaxName(raw) {
  const name = String(raw || "").trim().replace(/\s+/g, " ");
  if (!name) return null;
  let last, first;
  if (name.includes("/")) {
    [last, first = ""] = name.split("/", 2).map(x => x.trim());
  } else {
    // No slash (typed by hand): treat the final word as the surname
    const parts = name.split(" ");
    last = parts.length > 1 ? parts.pop() : parts[0];
    first = parts.length ? parts.join(" ") : "";
  }
  const firstWords = first.split(" ").filter(w => w && !NAME_TITLES.has(w.toUpperCase().replace(/\./g, "")));
  if (!last) return null;
  return { last: titleCaseName(last), initial: firstWords[0] ? firstWords[0][0].toUpperCase() : "" };
}

export function signNamesFromPassengers(passengers) {
  const bySurname = new Map();
  for (const p of passengers || []) {
    const n = splitPaxName(p?.name);
    if (!n) continue;
    const key = n.last.toUpperCase();
    if (!bySurname.has(key)) bySurname.set(key, { last: n.last, people: [] });
    bySurname.get(key).people.push(n);
  }
  return [...bySurname.values()]
    .sort((a, b) => a.last.localeCompare(b.last))
    .map(({ last, people }) => people.length > 1
      ? `${last} Party of ${people.length}`
      : (people[0].initial ? `${people[0].initial}. ${last}` : last));
}
